import {
  Form,
  Input,
  Checkbox,
  Button,
  Tabs,
  Alert,
  Message,
} from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { IconLock, IconUser } from '@arco-design/web-react/icon';
import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import useStorage from '@/utils/useStorage';
import useLocale from '@/utils/useLocale';
import { signIn } from '@/utils/supabaseAuth';
import {
  clearDingtalkCallbackPayloadFromUrl,
  consumeReturnUrl,
  type DingtalkAuthSession,
  handleDingtalkCallback,
  hasDingtalkCallbackPayload,
  initEmbeddedDingtalkLogin,
  prepareStateForEmbeddedLogin,
  readDingtalkCallbackPayload,
  redirectToDingtalk,
} from '@/utils/dingtalkAuth';
import { createLogger } from '@/utils/logger';
import locale from './locale';
import styles from './style/index.module.less';

const logger = createLogger('LoginForm');
const DINGTALK_QR_CONTAINER_ID = 'dingtalk-qr-login-container';

type LoginTabKey = 'dingtalk' | 'email';

interface EmailLoginFormData {
  userName: string;
  password: string;
}

function resolveReturnUrl(): string {
  const params = new URLSearchParams(window.location.search);
  const returnUrl = params.get('returnUrl');
  if (!returnUrl) {
    return '/';
  }

  if (returnUrl.startsWith('/')) {
    return returnUrl;
  }

  return '/';
}

async function establishDingtalkSession(session: DingtalkAuthSession): Promise<void> {
  const { data, error } = await supabase.auth.setSession({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  });

  if (error) {
    throw new Error(`写入登录会话失败: ${error.message}`);
  }

  const user = data.user || session.user;
  localStorage.setItem('userStatus', 'login');
  localStorage.setItem('token', session.access_token);

  if (user?.id) {
    localStorage.setItem('userId', user.id);
  } else {
    localStorage.removeItem('userId');
  }
  if (user?.email) {
    localStorage.setItem('userEmail', user.email);
  } else {
    localStorage.removeItem('userEmail');
  }
}

export default function LoginForm() {
  const formRef = useRef<FormInstance>();
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<LoginTabKey>('dingtalk');
  const [dingtalkMessage, setDingtalkMessage] = useState('');
  const [dingtalkError, setDingtalkError] = useState('');
  const [embeddedReason, setEmbeddedReason] = useState('');
  const [isHandlingDingtalkCallback, setIsHandlingDingtalkCallback] =
    useState(false);
  const [loginParams, setLoginParams, removeLoginParams] =
    useStorage('loginParams');
  const [rememberPassword, setRememberPassword] = useState(!!loginParams);

  const t = useLocale(locale);

  function afterLoginSuccess(params: EmailLoginFormData) {
    if (rememberPassword) {
      setLoginParams(JSON.stringify(params));
    } else {
      removeLoginParams();
    }

    window.location.href = resolveReturnUrl();
  }

  async function handleEmailLogin(params: EmailLoginFormData) {
    setErrorMessage('');
    setLoading(true);

    try {
      const result = await signIn({
        email: params.userName,
        password: params.password,
      });

      if (result.success) {
        afterLoginSuccess(params);
      } else {
        setErrorMessage(result.error || t['login.form.login.errMsg']);
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : t['login.form.login.errMsg'];
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  }

  function onSubmitClick() {
    formRef.current
      ?.validate()
      .then((values) => {
        handleEmailLogin(values as EmailLoginFormData);
      })
      .catch(() => {
        // 表单校验失败时不做额外处理
      });
  }

  function handleRedirectDingtalkLogin() {
    redirectToDingtalk(resolveReturnUrl());
  }

  useEffect(() => {
    const shouldRememberPassword = !!loginParams;
    setRememberPassword(shouldRememberPassword);

    if (formRef.current && shouldRememberPassword) {
      const parsedParams = JSON.parse(loginParams);
      formRef.current.setFieldsValue(parsedParams);
    }
  }, [loginParams]);

  useEffect(() => {
    const payload = readDingtalkCallbackPayload();
    if (!hasDingtalkCallbackPayload(payload)) {
      return;
    }

    setActiveTab('dingtalk');
    setIsHandlingDingtalkCallback(true);
    setDingtalkError('');
    setDingtalkMessage('正在处理钉钉登录结果...');

    const run = async () => {
      try {
        const result = await handleDingtalkCallback(payload);
        const callbackReturnUrl = payload.state
          ? consumeReturnUrl(payload.state)
          : null;

        if (callbackReturnUrl) {
          logger.debug('恢复扫码登录 returnUrl', { callbackReturnUrl });
        }

        if (result.sessionEstablished && result.session) {
          await establishDingtalkSession(result.session);
          window.location.href = callbackReturnUrl || resolveReturnUrl();
          return;
        }

        const message =
          result.message || '账号已匹配成功，当前环境尚未启用会话建立';
        setDingtalkMessage(message);
        Message.info(message);
      } catch (error) {
        const message = error instanceof Error ? error.message : '钉钉登录失败';
        setDingtalkError(message);
        setDingtalkMessage('');
      } finally {
        clearDingtalkCallbackPayloadFromUrl();
        setIsHandlingDingtalkCallback(false);
      }
    };

    run();
  }, []);

  useEffect(() => {
    if (activeTab !== 'dingtalk') {
      return;
    }

    const state = prepareStateForEmbeddedLogin();
    const result = initEmbeddedDingtalkLogin({
      containerId: DINGTALK_QR_CONTAINER_ID,
      state,
      onSuccess: (payload) => {
        const query = new URLSearchParams();
        if (payload.authCode) {
          query.set('authCode', payload.authCode);
        }
        if (payload.code) {
          query.set('code', payload.code);
        }
        if (payload.state) {
          query.set('state', payload.state);
        }

        if (query.toString()) {
          window.location.href = `${window.location.pathname}?${query.toString()}`;
        }
      },
      onError: (message) => {
        setDingtalkError(message);
      },
    });

    if (!result.enabled && result.reason) {
      setEmbeddedReason(result.reason);
      return;
    }

    setEmbeddedReason('');
  }, [activeTab]);

  return (
    <div className={styles['login-form-wrapper']}>
      <div className={styles['login-form-title']}>{t['login.form.title']}</div>
      <Tabs
        activeTab={activeTab}
        onChange={(key) => setActiveTab(key as LoginTabKey)}
        className={styles['login-tabs']}
      >
        <Tabs.TabPane key="dingtalk" title="钉钉登录">
          <div className={styles['dingtalk-login-panel']}>
            {!embeddedReason ? (
              <div
                id={DINGTALK_QR_CONTAINER_ID}
                className={styles['dingtalk-qrcode-container']}
              />
            ) : null}
            <div className={styles['dingtalk-login-tips']}>
              请使用钉钉扫码授权登录。
            </div>
            {embeddedReason ? (
              <Alert
                type="warning"
                showIcon
                content={embeddedReason}
                className={styles['dingtalk-login-alert']}
              />
            ) : null}
            {dingtalkError ? (
              <Alert
                type="error"
                showIcon
                content={dingtalkError}
                className={styles['dingtalk-login-alert']}
              />
            ) : null}
            {dingtalkMessage ? (
              <Alert
                type="info"
                showIcon
                content={dingtalkMessage}
                className={styles['dingtalk-login-alert']}
              />
            ) : null}
            <Button
              type="text"
              onClick={handleRedirectDingtalkLogin}
              loading={isHandlingDingtalkCallback}
              className={styles['dingtalk-login-fallback-btn']}
            >
              使用跳转授权登录
            </Button>
          </div>
        </Tabs.TabPane>

        <Tabs.TabPane key="email" title="邮箱登录">
          <div className={styles['login-form-error-msg']}>{errorMessage}</div>
          <Form
            className={styles['login-form']}
            layout="vertical"
            ref={formRef}
            initialValues={{ userName: '', password: '' }}
          >
            <Form.Item
              field="userName"
              label="邮箱"
              rules={[
                { required: true, message: '邮箱不能为空' },
                { type: 'email', message: '请输入有效的邮箱地址' },
              ]}
            >
              <Input
                prefix={<IconUser />}
                placeholder="请输入邮箱（例如：admin@test.com）"
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
            <Form.Item
              field="password"
              rules={[{ required: true, message: t['login.form.password.errMsg'] }]}
            >
              <Input.Password
                prefix={<IconLock />}
                placeholder={t['login.form.password.placeholder']}
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
            <div className={styles['login-form-password-actions']}>
              <Checkbox checked={rememberPassword} onChange={setRememberPassword}>
                {t['login.form.rememberPassword']}
              </Checkbox>
            </div>
            <Button
              type="primary"
              long
              onClick={onSubmitClick}
              loading={loading}
              className={styles['login-form-submit-btn']}
            >
              {t['login.form.login']}
            </Button>
          </Form>
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
}
